import { useEffect, useMemo, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errMsg } from "@/lib/api";

const aud = (c) => `$${(c / 100).toFixed(2)}`;
const pad = (n) => String(n).padStart(2, "0");

const lastMonths = () => {
  const now = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return { value: `${d.getFullYear()}-${pad(d.getMonth() + 1)}`, label: d.toLocaleDateString("en-AU", { month: "long", year: "numeric" }) };
  });
};

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const downloadCsv = (r) => {
  const lines = [
    ["Pitchpaper GST sales report", r.month, `Dates in ${r.timezone}`],
    [],
    ["Total sales incl. GST (G1)", aud(r.total_sales_cents)],
    ["GST on sales (1A)", aud(r.gst_cents)],
    ["GST-free sales (G2/G3)", aud(r.gst_free_sales_cents)],
    ["Sales excl. GST", aud(r.net_sales_cents)],
    ["Number of sales", r.count],
    [],
    ["Date", "Invoice", "Customer", "Item", "Total incl. GST", "GST", "Currency"],
    ...r.rows.map((x) => [x.date_local, x.number, x.customer_email, x.description, (x.total_cents / 100).toFixed(2), (x.gst_cents / 100).toFixed(2), x.currency.toUpperCase()]),
  ];
  const blob = new Blob([lines.map((l) => l.map(csvCell).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `pitchpaper-gst-${r.month}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
};

const Box = ({ label, value, testId, strong }) => (
  <div className={`rounded-xl border p-4 ${strong ? "border-thread bg-white" : "border-paper-border bg-white"}`}>
    <p className="font-mono text-[10px] uppercase tracking-widest text-taupe">{label}</p>
    <p className="mt-1 font-serif text-2xl text-ink" data-testid={testId}>{value}</p>
  </div>
);

export const GstReport = ({ api }) => {
  const months = useMemo(lastMonths, []);
  const [month, setMonth] = useState(months[0].value);
  const [rep, setRep] = useState(null);

  useEffect(() => {
    setRep(null);
    api.get("/admin/gst-report", { params: { month } }).then((r) => setRep(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [api, month]);

  return (
    <section data-testid="gst-report">
      <div className="mt-14 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl text-ink">GST sales report</h2>
          <p className="mt-1 text-sm text-ink-soft">Monthly figures for your BAS. Dates use Sydney time.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="field w-48" data-testid="gst-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{months.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`gst-month-${m.value}`}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
          <button className="btn-thread" onClick={() => downloadCsv(rep)} disabled={!rep} data-testid="gst-download-csv">
            <Download className="h-4 w-4" /> Download CSV
          </button>
        </div>
      </div>
      {!rep ? <Loader2 className="mt-6 h-6 w-6 animate-spin text-thread" /> : (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Box label="Total sales incl. GST (G1)" value={aud(rep.total_sales_cents)} testId="gst-total-sales" />
            <Box label="GST on sales (1A)" value={aud(rep.gst_cents)} testId="gst-collected" strong />
            <Box label="GST-free sales" value={aud(rep.gst_free_sales_cents)} testId="gst-free-sales" />
            <Box label="Number of sales" value={rep.count} testId="gst-sales-count" />
          </div>
          {rep.other_currency_count > 0 && <p className="mt-2 text-xs text-amber-700">{rep.other_currency_count} sale(s) in other currencies are listed below but not included in the totals.</p>}
          <div className="mt-4 overflow-x-auto rounded-xl border border-paper-border bg-white">
            <Table data-testid="gst-table">
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Invoice</TableHead><TableHead>Customer</TableHead><TableHead>Total</TableHead><TableHead>GST</TableHead></TableRow></TableHeader>
              <TableBody>
                {rep.rows.length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-ink-soft">No sales this month.</TableCell></TableRow>}
                {rep.rows.map((x) => (
                  <TableRow key={x.number} data-testid={`gst-row-${x.number}`}>
                    <TableCell className="whitespace-nowrap">{x.date_local}</TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-xs">{x.number}</TableCell>
                    <TableCell>{x.customer_email}</TableCell>
                    <TableCell className="whitespace-nowrap">{aud(x.total_cents)} {x.currency.toUpperCase()}</TableCell>
                    <TableCell className="whitespace-nowrap">{aud(x.gst_cents)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="mt-2 text-xs text-taupe">These are your Pitchpaper sales figures. Check them with your accountant before you lodge. Refunds aren't subtracted.</p>
        </>
      )}
    </section>
  );
};
